import { Component, ElementRef, ViewChild, forwardRef, input } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

/** One toolbar button. `stateful` buttons light up while the caret sits in matching text. */
interface EditorTool {
  command: string;
  icon: string;
  label: string;
  stateful: boolean;
}

/**
 * A minimal WYSIWYG editor: the author composes visually and the control's value is the HTML the
 * composition produces, ready to post to the backend.
 *
 * Built in-repo on `contenteditable` + `document.execCommand` rather than pulling in Quill or
 * CKEditor, because the project deliberately stays on Angular Material with no extra UI kits.
 * `execCommand` is formally deprecated but is still the only cross-browser way to drive rich-text
 * editing without a library, and every current browser implements the commands used here. If this
 * grows beyond the toolbar below, that is the point to reconsider a real editor dependency.
 *
 * Registers as a `ControlValueAccessor`, so it drops into a reactive form via `formControlName`
 * like any Material input.
 */
@Component({
  selector: 'app-rich-text-editor',
  imports: [FormsModule, MatIconModule, MatTooltipModule],
  templateUrl: './rich-text-editor.component.html',
  styleUrl: './rich-text-editor.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RichTextEditorComponent),
      multi: true
    }
  ]
})
export class RichTextEditorComponent implements ControlValueAccessor {

  /** Shown in the editing surface while it is empty — `contenteditable` has no `placeholder`. */
  readonly placeholder = input<string>('Compose your message…');

  @ViewChild('surface') private surface!: ElementRef<HTMLDivElement>;

  /**
   * The toolbar, in rendered order. Grouped so the template can draw a divider between clusters
   * rather than one undifferentiated row of icons.
   */
  protected readonly TOOL_GROUPS: ReadonlyArray<ReadonlyArray<EditorTool>> = [
    [
      { command: 'bold', icon: 'format_bold', label: 'Bold', stateful: true },
      { command: 'italic', icon: 'format_italic', label: 'Italic', stateful: true },
      { command: 'underline', icon: 'format_underlined', label: 'Underline', stateful: true }
    ],
    [
      { command: 'justifyLeft', icon: 'format_align_left', label: 'Align left', stateful: true },
      { command: 'justifyCenter', icon: 'format_align_center', label: 'Align centre', stateful: true },
      { command: 'justifyRight', icon: 'format_align_right', label: 'Align right', stateful: true }
    ],
    [
      { command: 'insertUnorderedList', icon: 'format_list_bulleted', label: 'Bulleted list', stateful: true },
      { command: 'insertOrderedList', icon: 'format_list_numbered', label: 'Numbered list', stateful: true }
    ],
    [
      { command: 'removeFormat', icon: 'format_clear', label: 'Clear formatting', stateful: false }
    ],
    [
      { command: 'undo', icon: 'undo', label: 'Undo', stateful: false },
      { command: 'redo', icon: 'redo', label: 'Redo', stateful: false }
    ]
  ];

  /** Which stateful commands are currently active, so their buttons can be highlighted. */
  protected activeCommands: Record<string, boolean> = {};

  protected isEmpty: boolean = true;
  protected isDisabled: boolean = false;

  /** True while the inline "insert link" bar is collecting a URL. */
  protected linkBarOpen: boolean = false;
  protected linkUrl: string = '';

  /** The selection to restore when the link bar applies — opening the bar moves focus away. */
  private savedRange: Range | null = null;

  private value: string = '';
  private onChange: (value: string) => void = () => { };
  private onTouched: () => void = () => { };

  // ─────────────────────────── ControlValueAccessor ───────────────────────────

  writeValue(value: string): void {
    this.value = value ?? '';

    // Never rewrite the DOM while the author is typing in it: assigning innerHTML collapses the
    // caret to the start of the surface, which makes the field unusable.
    if (this.surface && document.activeElement !== this.surface.nativeElement) {
      this.surface.nativeElement.innerHTML = this.value;
      this.syncEmptyState();
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  // ─────────────────────────── Editing ───────────────────────────

  protected onInput(): void {
    const html: string = this.surface.nativeElement.innerHTML;

    // An "empty" contenteditable still reports markup — `<br>` or `<div><br></div>` — which would
    // satisfy Validators.required. Normalise it so the field validates as genuinely empty.
    this.value = this.hasContent() ? html : '';

    this.syncEmptyState();
    this.onChange(this.value);
  }

  protected onBlur(): void {
    this.onTouched();
  }

  /**
   * Keeps the toolbar's highlight in step with wherever the caret is. Driven from user events
   * rather than change detection so `queryCommandState` is not queried on every CD pass.
   */
  protected refreshToolbarState(): void {
    const active: Record<string, boolean> = {};

    for (const group of this.TOOL_GROUPS) {
      for (const tool of group) {
        if (tool.stateful) {
          active[tool.command] = this.queryState(tool.command);
        }
      }
    }

    this.activeCommands = active;
  }

  private queryState(command: string): boolean {
    try {
      return document.queryCommandState(command);
    } catch {
      // Safari throws for some commands when the selection is outside the surface.
      return false;
    }
  }

  /**
   * `mousedown` on a toolbar button is prevented in the template, so the selection survives and
   * the command applies to the text the author had highlighted.
   */
  protected runCommand(command: string): void {
    if (this.isDisabled) {
      return;
    }

    this.surface.nativeElement.focus();

    // Emit inline `style` attributes rather than legacy <font> tags — inline styles are what
    // survive email clients, and what the preview iframe renders.
    document.execCommand('styleWithCSS', false, 'true');
    document.execCommand(command);

    this.onInput();
    this.refreshToolbarState();
  }

  protected applyTextColor(event: Event): void {
    const color: string = (event.target as HTMLInputElement).value;

    this.restoreSelection();
    document.execCommand('styleWithCSS', false, 'true');
    document.execCommand('foreColor', false, color);

    this.onInput();
  }

  // ─────────────────────────── Links ───────────────────────────

  protected openLinkBar(): void {
    if (this.isDisabled) {
      return;
    }

    this.saveSelection();
    this.linkUrl = '';
    this.linkBarOpen = true;
  }

  protected applyLink(): void {
    const url: string = this.linkUrl.trim();

    if (!url) {
      this.closeLinkBar();
      return;
    }

    // A bare domain would otherwise resolve relative to the app's own origin.
    const href: string = /^(https?:|mailto:)/i.test(url) ? url : `https://${url}`;

    this.restoreSelection();
    document.execCommand('createLink', false, href);

    this.onInput();
    this.closeLinkBar();
  }

  protected closeLinkBar(): void {
    this.linkBarOpen = false;
    this.linkUrl = '';
    this.savedRange = null;
  }

  // ─────────────────────────── Paste ───────────────────────────

  /**
   * Pasting from Word or a browser drags in stylesheets, classes and often whole table scaffolds
   * that render unpredictably in email clients. Taking the plain text and letting the author
   * re-apply formatting with the toolbar keeps the produced HTML something we can reason about.
   */
  protected onPaste(event: ClipboardEvent): void {
    event.preventDefault();

    const text: string = event.clipboardData?.getData('text/plain') ?? '';

    if (text) {
      document.execCommand('insertText', false, text);
      this.onInput();
    }
  }

  // ─────────────────────────── Selection helpers ───────────────────────────

  private saveSelection(): void {
    const selection: Selection | null = window.getSelection();

    this.savedRange = selection && selection.rangeCount
      ? selection.getRangeAt(0).cloneRange()
      : null;
  }

  private restoreSelection(): void {
    this.surface.nativeElement.focus();

    if (!this.savedRange) {
      return;
    }

    const selection: Selection | null = window.getSelection();

    if (selection) {
      selection.removeAllRanges();
      selection.addRange(this.savedRange);
    }
  }

  private hasContent(): boolean {
    const element: HTMLDivElement = this.surface.nativeElement;

    // An image alone is content even though it contributes no text.
    return element.textContent!.trim().length > 0 || element.querySelector('img') !== null;
  }

  private syncEmptyState(): void {
    this.isEmpty = !this.hasContent();
  }
}
